import React, { ComponentType, useEffect, useState } from 'react';
import { LightProduct, Product, Variant } from 'modules/catalogue/models';
import { productService } from 'modules/catalogue/services';
import { getDefault, readCMSValue } from 'modules/shared-cms/utils';
import { getProductCDNUrl } from 'modules/shared/utils';
import { CmsProductListProps } from '../types';

export interface WithProductsFetchingInjectedProps {
    products: LightProduct[];
}

const findPrice = (
    stringId: string,
    variants?: Variant[],
    collections?: LightProduct[],
    favorites?: LightProduct[],
    price?: number,
): number => {
    if (price) {
        return price;
    }

    if (variants && variants.length === 1) {
        return variants[0].price;
    }

    let variant = (variants || []).find(v => v.id === stringId);

    if (!variant) {
        variant = collections ? collections.find(v => v.id === stringId) : undefined;
    }
    if (!variant) {
        variant = favorites ? favorites.find(v => v.id === stringId) : undefined;
    }

    if (variant) {
        return variant.price;
    }

    return 0;
};

export const WithFetchProducts = <P extends CmsProductListProps>(LoaderComponent?: React.FunctionComponent) => (
    Component: ComponentType<Omit<P, 'products'> & WithProductsFetchingInjectedProps>,
): React.FC<P> => {
    const WithProductsFetchingComponent: React.FC<P> = ({ products, ...props }: P) => {
        const [data, setData] = useState<LightProduct[]>([]);
        const [isLoading, setIsLoading] = useState<boolean>(true);

        useEffect(() => {
            const fetchData = async (): Promise<void> => {
                try {
                    const readProducts = getDefault(readCMSValue(products), []);

                    if (!readProducts || readProducts.length === 0) {
                        setData([]);
                    }

                    const datas = await Promise.all(
                        readProducts.map(p => {
                            const secureGet = async (): Promise<Product | undefined> => {
                                try {
                                    return await productService.get({ id: p.id });
                                } catch {
                                    return undefined;
                                }
                            };

                            return secureGet();
                        }),
                    );

                    const finalProductList: LightProduct[] = datas
                        .filter((p): p is Product => !!p)
                        .map(p => {
                            const stringId = `${p.id}`;
                            const fileName = getProductCDNUrl(p.images[0].fileName) || '';
                            const lightProduct: LightProduct = {
                                id: stringId,
                                variantId: p.variants && p.variants.length === 1 ? p.variants[0].id : '',
                                name: p.name,
                                brand: p.brand.name,
                                price: findPrice(stringId, p.variants, p.collections, p.favorites, p.price),
                                fileName,
                            };

                            return lightProduct;
                        });

                    setData(finalProductList);
                } catch (err) {
                    //  setError(err);
                } finally {
                    setIsLoading(false);
                }
            };

            fetchData();
        }, [products]);

        if (isLoading) {
            if (LoaderComponent) {
                return <LoaderComponent />;
            }

            return <div>Chargement en cours...</div>;
        }

        return <Component {...props} products={data} />;
    };

    return WithProductsFetchingComponent;
};
